import { useProjectSourceStates } from '@/viewmodel/features/sourceState/hooks/useProjectSourceStates';
import { rawSourceState } from '@/viewmodel/features/sourceState/mappers/rawStatus';
import { toSharedTableStateView } from '@/viewmodel/features/sourceState/mappers/shared';
import {
  toSourceStateView,
  toTableStateView,
} from '@/viewmodel/features/sourceState/mappers/snapshot';
import { toSourceListStats } from '@/viewmodel/features/sourceState/mappers/stats';
import { toSourceStatusView } from '@/viewmodel/features/sourceState/mappers/status';
import type {
  SharedTableStateView,
  SourceListStatsView,
  SourceStateView,
  SourceStatusView,
  TableStateView,
} from '@/viewmodel/features/sourceState/types';
import { useMemo } from 'react';

export interface SourceStatesViewModel {
  readonly stats: SourceListStatsView;
  readonly statusById: ReadonlyMap<string, SourceStatusView>;
  readonly hasConnectedSource: boolean;
  readonly sharedTables: readonly SharedTableStateView[];
  readonly processedTables: readonly TableStateView[];
}

export function useSourceStatesViewModel(
  projectId: string | undefined,
  liveUpdates: boolean,
): SourceStatesViewModel {
  const { snapshot } = useProjectSourceStates(projectId, liveUpdates);

  return useMemo(() => {
    const sources = snapshot?.sources ?? [];
    const statusById = new Map(
      sources.map((source): [string, SourceStatusView] => [
        source.source_id,
        toSourceStatusView(source.state),
      ]),
    );

    return {
      stats: toSourceListStats([...statusById.values()]),
      statusById,
      hasConnectedSource: [...statusById.values()].some((status) => status.isConnected),
      sharedTables: (snapshot?.shared_tables ?? []).map(toSharedTableStateView),
      processedTables: sources.flatMap((source) => source.tables.map(toTableStateView)),
    };
  }, [snapshot]);
}

interface SourceStateInput {
  readonly projectId: string | undefined;
  readonly sourceId: string;
  readonly rawStatus: string | undefined;
  readonly liveUpdates: boolean;
  readonly awaitingRun: boolean;
}

export function useSourceStateViewModel({
  projectId,
  sourceId,
  rawStatus,
  liveUpdates,
  awaitingRun,
}: SourceStateInput): SourceStateView {
  const { snapshot } = useProjectSourceStates(projectId, liveUpdates, awaitingRun);
  const source = snapshot?.sources.find((candidate) => candidate.source_id === sourceId);

  return useMemo(
    () => (source ? toSourceStateView(source) : rawSourceState(rawStatus)),
    [source, rawStatus],
  );
}
